import React from 'react'
import { Box, Grid } from '@material-ui/core'
import { PageHeader } from 'components/page-header'
import { useQuery } from '@apollo/client'
import { GET_COACH_ANALYTICS } from './query'
import useStyles from './styles'
import {
  PaymentsByMonthChart,
  EarningsCard,
  PrivateLessonRevenueCard,
  PrivateLessonMonthlyChart,
  TopBookedLessonsChart,
  GroupEventRevenueCard,
  GroupEventMonthlyChart,
  TopGroupEventsChart,
  StatCard,
  LeadCard,
  ConversionRateCard,
} from './components'

// Demo data for the private lesson sections
const DEMO_PRIVATE_LESSON_DATA = {
  revenue: 24850.75,
  totalTypes: 5,
  byMonth: [
    { month: '2023-05', count: 15 },
    { month: '2023-06', count: 18 },
    { month: '2023-07', count: 22 },
    { month: '2023-08', count: 25 },
    { month: '2023-09', count: 20 },
    { month: '2023-10', count: 28 },
    { month: '2023-11', count: 30 },
    { month: '2023-12', count: 35 },
    { month: '2024-01', count: 32 },
    { month: '2024-02', count: 36 },
    { month: '2024-03', count: 42 },
  ],
  topBooked: [
    {
      lessonType: { title: 'Private Instruction', id: '1' },
      lessonCount: 186,
      uniqueStudentCount: 73,
      earnings: 18600,
    },
    {
      lessonType: { title: 'Private Instruction - FNF', id: '2' },
      lessonCount: 24,
      uniqueStudentCount: 15,
      earnings: 2400,
    },
    {
      lessonType: { title: '2 hours Private Instruction', id: '3' },
      lessonCount: 18,
      uniqueStudentCount: 10,
      earnings: 2700,
    },
    {
      lessonType: { title: 'Private Instruction - Media', id: '4' },
      lessonCount: 8,
      uniqueStudentCount: 6,
      earnings: 800,
    },
    {
      lessonType: { title: 'Holiday Lesson Pack', id: '5' },
      lessonCount: 5,
      uniqueStudentCount: 3,
      earnings: 350,
    },
  ],
}

const CoachAnalytics = () => {
  const classes = useStyles()
  const { data, loading, error } = useQuery(GET_COACH_ANALYTICS)

  // Get data from API or use demo data if none available
  const privateLessonData =
    data?.pgaCoachAnalytics?.privateLessonTypes || DEMO_PRIVATE_LESSON_DATA

  // Get group event data directly from the API
  const groupEventData = data?.pgaCoachAnalytics?.groupEvents

  // Calculate total lessons from the data
  const totalLessons = 198 // This would ideally come from the API

  return (
    <>
      <PageHeader title="PGA Coach Analytics" fixedMobileActions />
      <Box className={classes.container}>
        {/* First row - Earnings cards */}
        <Grid container spacing={3} style={{ marginBottom: '24px' }}>
          <Grid item xs={12} md={4}>
            <EarningsCard
              data={{
                total: data?.pgaCoachAnalytics?.earnings?.total,
                source: 'Earnings through PGA Coach',
              }}
              loading={loading}
              error={error}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <PrivateLessonRevenueCard
              data={privateLessonData}
              loading={loading}
              error={error}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <GroupEventRevenueCard
              data={groupEventData}
              loading={loading}
              error={error}
            />
          </Grid>
        </Grid>

        {/* Second row - Leads cards */}
        <Grid container spacing={3} style={{ marginBottom: '24px' }}>
          <Grid item xs={12} md={4}>
            <LeadCard
              title="Total Leads"
              value={data?.pgaCoachAnalytics?.leads?.totalCount}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LeadCard
              title="Unviewed Leads"
              value={data?.pgaCoachAnalytics?.leads?.unviewedCount}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ConversionRateCard
              value={`${data?.pgaCoachAnalytics?.leads?.conversionRate}%`}
            />
          </Grid>
        </Grid>

        {/* Third row - Payments chart and lesson stats */}
        <Grid container spacing={3} style={{ marginBottom: '24px' }}>
          <Grid item xs={12} md={8}>
            <Box className={classes.chart}>
              <PaymentsByMonthChart
                data={data?.pgaCoachAnalytics?.earnings?.byMonth}
                title="Payments By Month"
                loading={loading}
                error={error}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <StatCard
                  title="Average Lesson Price"
                  value="$125.00"
                  subtitle="Per private lesson"
                />
              </Grid>
              <Grid item>
                <StatCard
                  title="Highest Revenue Month"
                  value="$3,250.00"
                  subtitle="March 2024"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Fourth row - Monthly charts */}
        <Grid container spacing={3} style={{ marginBottom: '24px' }}>
          <Grid item xs={12} md={6}>
            <Box className={classes.chart}>
              <PrivateLessonMonthlyChart
                data={privateLessonData.byMonth}
                title="Private Lessons By Month"
                loading={loading}
                error={error}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={classes.chart}>
              <GroupEventMonthlyChart
                data={groupEventData?.byMonth}
                title="Group Event Enrollments By Month"
                loading={loading}
                error={error}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Fifth row - Pie charts */}
        <Grid container spacing={3} style={{ marginBottom: '24px' }}>
          <Grid item xs={12} md={6}>
            <Box className={classes.chart} style={{ minHeight: '380px' }}>
              <TopBookedLessonsChart
                data={privateLessonData.topBooked}
                loading={loading}
                error={error}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={classes.chart} style={{ minHeight: '380px' }}>
              <TopGroupEventsChart
                data={groupEventData?.topEarning}
                loading={loading}
                error={error}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Sixth row - Bottom stat cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Average Participants"
              value="8"
              subtitle="Per group event"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Highest Revenue Event"
              value="$1,200.00"
              subtitle="Whiskey & Wedges (Dec 2023)"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Total Lessons"
              value={totalLessons}
              subtitle="All-time lessons"
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default CoachAnalytics
